import * as React from 'react'
import { cn } from '@/utilities/cn'
import { Container } from '@/components/Container'

export interface ListPageLayoutProps {
  title: string
  description?: string
  /** Rendered into the grid slot — typically a list of cards. */
  children?: React.ReactNode
  /** Rendered when `children` is empty / no items. */
  emptyState?: React.ReactNode
  /**
   * Whether the children slot is empty. Drives `emptyState` rendering.
   *
   * Why an explicit prop instead of introspecting `children`: callers
   * usually pass a wrapper element (`<div className="grid"> {items.map…}
   * </div>`) which is always truthy regardless of whether any items
   * mapped through. We can't tell emptiness from `children: ReactNode`,
   * so the caller has to assert it via this prop. Both `isEmpty` and
   * the items list derive from the same server-side fetch in the same
   * render, so they cannot desync within a single SSR pass.
   */
  isEmpty?: boolean
  className?: string
}

export const ListPageLayout: React.FC<ListPageLayoutProps> = ({
  title,
  description,
  children,
  emptyState,
  isEmpty = false,
  className,
}) => {
  return (
    <Container className={cn('py-12 lg:py-16', className)}>
      <header className="text-center mb-12">
        <h1 className="text-4xl lg:text-5xl font-kadwa font-bold text-slate-800 mb-4">{title}</h1>
        {description ? (
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">{description}</p>
        ) : null}
      </header>
      {isEmpty ? emptyState : children}
    </Container>
  )
}

export default ListPageLayout
